<!-- ==============================
  File Name: UserList.vue
  Description: User List page
  -------------------------------
=============================== -->

<template>
  <div id="page-user-list">
    <vx-card
      ref="filterCard"
      title="Filters"
      class="user-list-filters mb-8"
      actionButtons
      @refresh="resetColFilters"
      @remove="resetColFilters"
    >
      <div class="vx-row">
        <div class="vx-col md:w-1/4 sm:w-1/2 w-full">
          <label class="text-sm opacity-75">Role</label>
          <v-select
            :options="roleOptions"
            :clearable="false"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            v-model="roleFilter"
            class="mb-4 md:mb-0"
          />
        </div>
      </div>
    </vx-card>

    <div class="vx-card p-6">
      <div class="flex flex-wrap items-center">
        <!-- ITEMS PER PAGE -->
        <div class="flex-grow">
          <vs-dropdown vs-trigger-click class="cursor-pointer">
            <div
              class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
            >
              <span class="mr-2" >
                {{ totalRecordCount === 0 ? 0 : currentPage * paginationPageSize - (paginationPageSize - 1) }} - {{ this.totalRecordCount - currentPage * paginationPageSize > 0 ? currentPage * paginationPageSize : totalRecordCount}} of {{ this.totalRecordCount }}
              </span>
              
              <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4"/>
            </div>
            <!-- <vs-button class="btn-drop" type="line" color="primary" icon-pack="feather" icon="icon-chevron-down"></vs-button> -->
            <vs-dropdown-menu>
              <vs-dropdown-item @click="gridApi.paginationSetPageSize(10)">
                <span>10</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="gridApi.paginationSetPageSize(20)">
                <span>20</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="gridApi.paginationSetPageSize(25)">
                <span>25</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="gridApi.paginationSetPageSize(30)">
                <span>30</span>
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </div>

        <vs-button
          color="primary"
          v-if="userInfo && userInfo.Role === 'Admin'"
          icon-pack="feather"
          icon="icon-plus"
          @click.native="$router.push('/apps/users/add')"
        >Add User</vs-button>
      </div>

      <!-- AgGrid Table -->
      <ag-grid-vue
        ref="agGridTable"
        :components="components"
        :gridOptions="gridOptions"
        class="ag-theme-material w-100 h-610 my-4 ag-grid-table"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="usersData"
        rowSelection="multiple"
        colResizeDefault="shift"
        :animateRows="true"
        :floatingFilter="true"
        :pagination="true"
        :paginationPageSize="paginationPageSize"
        :suppressPaginationPanel="true"
        :enableRtl="$vs.rtl"
      ></ag-grid-vue>

      <vs-pagination :total="totalPages" :max="7" v-model="currentPage"/>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import vSelect from "vue-select";
import * as moment from 'moment-timezone';
// Store Module
import moduleUserManagement from "@/store/user-management/moduleUserManagement.js";

// Cell Renderer
import CellRendererStatus from "./cell-renderer/CellRendererStatus.vue";
import CellRendererRole from "./cell-renderer/CellRendererRole.vue";
import CellRendererDate from "./cell-renderer/CellRendererDate.vue";
import CellRendererActions from "./cell-renderer/CellRendererActions.vue";
import "@/assets/css/main.css";

// Services
import UserService from "@/services/UserService";
import UtilService from "@/services/UtilService";

export default {
  components: {
    AgGridVue,
    vSelect,

    // Cell Renderer
    CellRendererRole,
    CellRendererDate,
    CellRendererStatus,
    CellRendererActions
  },
  data() {
    return {
      userInfo: null,
      users: [],
      // Filter Options
      roleFilter: { label: "All", value: "all" },
      roleOptions: [
        { label: "All", value: "all" },
        { label: "Admin", value: "Admin" },
        { label: "Manager", value: "Manager" },
        { label: "Partner Admin", value: "Partner Admin" },
        { label: "Partner Staff", value: "Partner Staff" }
      ],

      searchQuery: "",
      totalRecordCount: 0,
      // AgGrid
      gridApi: null,
      gridOptions: {
        onFilterChanged: (event) => { 
          this.totalRecordCount = (this.users && this.users.length === event.api.paginationGetRowCount())? this.users.length : event.api.paginationGetRowCount()
        },
        onGridReady(params) {
          this.gridApi = params.api;
          this.columnApi = params.columnApi;
          this.gridApi.sizeColumnsToFit();
          window.onresize = () => {
            this.gridApi.sizeColumnsToFit();
          }
        }
      },
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMenu: true
      },
      columnDefs: [
        
        {
          headerName: "Email",
          field: "Email",
          width: 300,
          filter: true,
          checkboxSelection: false,
          headerCheckboxSelectionFilteredOnly: false,
          headerCheckboxSelection: false
        },
        {
          headerName: "Name",
          field: "Name",
          filter: true,
          width: 230
        },
        {
          headerName: "Role",
          field: "Role",
          filter: true,
          width: 160,
          cellRendererFramework: "CellRendererRole"
        },
        {
          headerName: "Status",
          field: "Status",
          filter: true,
          width: 160,
          cellRendererFramework: "CellRendererStatus"
        },
        {
          headerName: "Created Date",
          field: "CreatedDate",
          filter: true,
          width: 180,
        }
      ],

      // Cell Renderer Components
      components: {
        CellRendererRole,
        CellRendererDate,
        CellRendererStatus,
        CellRendererActions
      }
    };
  },
  watch: {
    roleFilter(obj) {
      this.setColumnFilter("Role", obj.value);
    },
    statusFilter(obj) {
      this.setColumnFilter("Status", obj.value);
    },
    isVerifiedFilter(obj) {
      const val =
        obj.value === "all" ? "all" : obj.value === "yes" ? "true" : "false";
      this.setColumnFilter("is_verified", val);
    },
    departmentFilter(obj) {
      this.setColumnFilter("department", obj.value);
    }
  },
  computed: {
    usersData() {
      return this.users;
    },
    paginationPageSize() {
      if (this.gridApi) return this.gridApi.paginationGetPageSize();
      else return 10;
    },
    totalPages() {
      if (this.gridApi) return this.gridApi.paginationGetTotalPages();
      else return 0;
    },
    currentPage: {
      get() {
        if (this.gridApi) return this.gridApi.paginationGetCurrentPage() + 1;
        else return 1;
      },
      set(val) {
        this.gridApi.paginationGoToPage(val - 1);
      }
    }
  },
  methods: {
    setColumnFilter(column, val) {
      const filter = this.gridApi.getFilterInstance(column);
      let modelObj = null;

      if (val !== "all") {
        modelObj = { type: "startsWith", filter: val };
      }

      filter.setModel(modelObj);
      this.gridApi.onFilterChanged();
    },
    resetColFilters() {
      // Reset Grid Filter
      this.gridApi.setFilterModel(null);
      this.gridApi.onFilterChanged();

      // Reset Filter Options
      this.roleFilter = this.statusFilter = this.isVerifiedFilter = this.departmentFilter = {
        label: "All",
        value: "all"
      };

      this.$refs.filterCard.removeRefreshAnimation();
    },
    updateSearchQuery(val) {
      this.gridApi.setQuickFilter(val);
    },

    getUsers() {
      UserService.getAll().on("value", items => {
        let Users = [];
        items.forEach(item => {
          let key = item.key;
          let data = item.val();
          if(data && data['IsDeleted'] === 0){
            data['CreatedDate'] = UtilService.convertUTCToCurrentZone(data['CreatedDate'],this.userInfo.Timezone)
            Users.push({ ...data });
          }
        });
        this.totalRecordCount = Users.length;
        this.users = Users.sort(function(a, b) {
          return (
            new Date(
              UtilService.convertDDMMYYYYHHMMToYYYYMMDDHHMM(b.ModifiedDate)
            ).getTime() -
            new Date(
              UtilService.convertDDMMYYYYHHMMToYYYYMMDDHHMM(a.ModifiedDate)
            ).getTime()
          );
        });
      });
    }
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    if (this.$vs.rtl) {
      const header = this.$refs.agGridTable.$el.querySelector(
        ".ag-header-container"
      );
      header.style.left = `-${String(
        Number(header.style.transform.slice(11, -3)) + 9
      )}px`;
    }
  },
  created() {
    const userInfo = JSON.parse(localStorage.getItem("userInfo"));
    this.userInfo = userInfo;
    if (this.userInfo && this.userInfo.Role === "Admin") {
      this.columnDefs.push({
        headerName: "Actions",
        field: "",
        width: 100,
        cellRendererFramework: "CellRendererActions"
      });
    }
    this.getUsers();
  }
};
</script>

<style lang="scss">
#page-user-list {
  .user-list-filters {
    .vs__actions {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-58%);
    }
  }
}
.h-610 {
  height: 610px;
}
</style>
